import { Box, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { EXPIRY_MS, WARNING_THRESHOLD_MS } from "../constants";
import { useImpersonationSessions } from "../ImpersonationSessionsProvider";
import type { ImpersonationSession } from "../types";

const BALLOON_WIDTH = 220;
const BALLOON_HEIGHT = 36;

interface ImpersonationBalloonProps {
  session: ImpersonationSession;
}

function clampToViewport(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(x, window.innerWidth - BALLOON_WIDTH)),
    y: Math.max(0, Math.min(y, window.innerHeight - BALLOON_HEIGHT)),
  };
}

function getTooltipText(isExpired: boolean, isWarning: boolean): string {
  if (isExpired) {
    return "Session expired — click to dismiss";
  }

  if (isWarning) {
    return "Session expires soon";
  }

  return "Click to restore";
}

function getBackgroundColor(isExpired: boolean, isWarning: boolean): string {
  if (isExpired) {
    return "error.main";
  }

  if (isWarning) {
    return "warning.main";
  }

  return "primary.main";
}

export function ImpersonationBalloon({ session }: ImpersonationBalloonProps) {
  const { closeSession, restoreSession, updateBalloonPosition } = useImpersonationSessions();
  const [elapsed, setElapsed] = useState(Date.now() - session.startedAt);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - session.startedAt);
    }, 1_000);

    return () => {
      clearInterval(interval);
    };
  }, [session.startedAt]);

  const remainingMs = session.expiresAt ? session.expiresAt - Date.now() : EXPIRY_MS - elapsed;
  const isExpired = remainingMs <= 0;
  const isWarning = !isExpired && remainingMs <= WARNING_THRESHOLD_MS;
  const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60_000));

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      isDragging.current = true;
      hasDragged.current = false;
      dragOffset.current = {
        x: event.clientX - session.balloonPosition.x,
        y: event.clientY - session.balloonPosition.y,
      };
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [session.balloonPosition]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging.current) {
        return;
      }

      hasDragged.current = true;
      const clamped = clampToViewport(
        event.clientX - dragOffset.current.x,
        event.clientY - dragOffset.current.y
      );
      updateBalloonPosition(session.id, clamped);
    },
    [session.id, updateBalloonPosition]
  );

  const handleActivate = useCallback(() => {
    if (isExpired) {
      closeSession(session.id);
    } else {
      restoreSession(session.id);
    }
  }, [closeSession, isExpired, restoreSession, session.id]);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
    if (!hasDragged.current) {
      handleActivate();
    }
  }, [handleActivate]);

  return (
    <Tooltip title={getTooltipText(isExpired, isWarning)}>
      <Box
        component="button"
        tabIndex={0}
        aria-label={
          isExpired
            ? `Dismiss expired impersonation session for ${session.workerName}`
            : `Restore impersonation session for ${session.workerName}`
        }
        sx={(theme) => ({
          border: "none",
          font: "inherit",
          position: "fixed",
          left: session.balloonPosition.x,
          top: session.balloonPosition.y,
          zIndex: theme.zIndex.modal + 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingX: 2,
          paddingY: 1,
          width: BALLOON_WIDTH,
          height: BALLOON_HEIGHT,
          borderRadius: "24px",
          backgroundColor: getBackgroundColor(isExpired, isWarning),
          color: theme.palette.common.white,
          cursor: "pointer",
          boxShadow: 8,
          userSelect: "none",
          boxSizing: "border-box",
          transition: "background-color 0.3s",
          "&:hover": {
            boxShadow: 12,
          },
        })}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleActivate();
          }
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            pointerEvents: "none",
          }}
        >
          {session.workerName}
        </Box>
        <Box
          component="span"
          sx={{ opacity: 0.8, flexShrink: 0, fontSize: "0.875rem", pointerEvents: "none" }}
        >
          {isExpired ? "Expired" : `${remainingMinutes}m`}
        </Box>
      </Box>
    </Tooltip>
  );
}
